import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { ContentCopy, Refresh, Visibility, VisibilityOff } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useGetApiCredentialsHandler } from '../dataHandlers/getApiCredentialsHandler';
import {
  useGenerateOTPHandler,
  useViewSecretHandler,
  useRegenerateSecretHandler,
} from '../dataHandlers/mutateApiIntegrationHandler';
import OTPModal from './OTPModal';
import NewSecretModal from './NewSecretModal';

const APIIntegrationTab = () => {
  const dispatch = useDispatch();

  const [revealedSecret, setRevealedSecret] = useState('');
  const [newSecret, setNewSecret] = useState('');
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [newSecretModalOpen, setNewSecretModalOpen] = useState(false);
  const [otpAction, setOtpAction] = useState('');

  const { credentials, isLoading: isCredentialsLoading } = useGetApiCredentialsHandler();

  const { onSubmit: generateOTP, isPending: isGeneratingOTP } = useGenerateOTPHandler({
    onSuccess: () => {
      // OTP generated successfully
    },
  });

  const { onSubmit: viewSecret, isPending: isViewingSecret } = useViewSecretHandler({
    onSuccess: (data) => {
      setRevealedSecret(data.clientSecret);
      setOtpModalOpen(false);
    },
  });

  const { onSubmit: regenerateSecret, isPending: isRegeneratingSecret } = useRegenerateSecretHandler({
    onSuccess: (data) => {
      setNewSecret(data.newSecret);
      setNewSecretModalOpen(true);
      setOtpModalOpen(false);
      setRevealedSecret('');
    },
  });

  const isLoading = isCredentialsLoading || isGeneratingOTP || isViewingSecret || isRegeneratingSecret;

  const handleViewSecret = () => {
    setOtpAction('view_secret');
    setOtpModalOpen(true);
  };

  const handleRegenerateSecret = () => {
    setOtpAction('regenerate_secret');
    setOtpModalOpen(true);
    setRevealedSecret('');
  };

  const handleHideSecret = () => {
    setRevealedSecret('');
  };

  const handleCloseNewSecretModal = () => {
    setNewSecretModalOpen(false);
    setNewSecret('');
    setRevealedSecret('');
  };

  const handleOTPSubmit = (otp) => {
    if (otpAction === 'view_secret') {
      viewSecret(otp);
    } else if (otpAction === 'regenerate_secret') {
      regenerateSecret(otp);
    }
  };

  const handleGenerateOTP = () => {
    generateOTP(otpAction);
  };

  useEffect(() => {
    if (otpModalOpen && otpAction) {
      handleGenerateOTP();
    }
  }, [otpModalOpen, otpAction]);

  const clientId = credentials?.clientId || '';
  const maskedSecret = credentials?.maskedSecret || credentials?.clientSecret || '';

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      dispatch(
        showSnackbar({
          message: `${label} has been copied to clipboard`,
          severity: 'success',
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Failed to copy to clipboard',
          severity: 'error',
        }),
      );
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        p: 3,
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 2,
        backgroundColor: 'white',
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        API Integration
      </Typography>

      <Stack spacing={2}>
        <Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, minWidth: 120 }}>
              Client ID
            </Typography>
            <TextField
              fullWidth
              value={clientId}
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'grey.50',
                  '& fieldset': {
                    borderColor: 'grey.300',
                  },
                  '&:hover fieldset': {
                    borderColor: 'grey.400',
                  },
                },
              }}
            />
            <IconButton
              onClick={() => copyToClipboard(clientId, 'Client ID')}
              size="small"
              sx={{
                border: '1px solid',
                borderColor: 'grey.300',
                backgroundColor: 'grey.100',
                borderRadius: 1,
                minWidth: 40,
                height: 40,
                '&:hover': {
                  backgroundColor: 'grey.200',
                },
              }}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, minWidth: 120 }}>
              Client Secret
            </Typography>
            <TextField
              fullWidth
              value={revealedSecret || (credentials ? '••••••••••••••••••••••••••••••••' : '')}
              type={revealedSecret ? 'text' : 'password'}
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'grey.50',
                  '& fieldset': {
                    borderColor: 'grey.300',
                  },
                  '&:hover fieldset': {
                    borderColor: 'grey.400',
                  },
                },
              }}
            />
            {revealedSecret && (
              <IconButton
                onClick={() => copyToClipboard(revealedSecret, 'Client Secret')}
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.300',
                  backgroundColor: 'grey.100',
                  borderRadius: 1,
                  minWidth: 40,
                  height: 40,
                  '&:hover': {
                    backgroundColor: 'grey.200',
                  },
                }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Stack direction="row" spacing={2}>
            {revealedSecret ? (
              <Button
                variant="outlined"
                startIcon={<VisibilityOff />}
                onClick={handleHideSecret}
                disabled={isLoading}
                sx={{ minWidth: 140, textTransform: 'none' }}
              >
                Hide Secret
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<Visibility />}
                onClick={handleViewSecret}
                disabled={isLoading || !!revealedSecret}
                sx={{ minWidth: 140, textTransform: 'none' }}
              >
                {revealedSecret ? 'Secret Visible' : 'View Secret'}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRegenerateSecret}
              disabled={isLoading}
              sx={{ minWidth: 140, textTransform: 'none' }}
            >
              Regenerate Secret
            </Button>
          </Stack>
        </Box>
      </Stack>

      <OTPModal
        open={otpModalOpen}
        onClose={() => {
          setOtpModalOpen(false);
          // Hide revealed secret if user was viewing it and closes modal
          if (otpAction === 'view_secret') {
            setRevealedSecret('');
          }
        }}
        onSubmit={handleOTPSubmit}
        onGenerateOTP={handleGenerateOTP}
        action={otpAction}
        isLoading={isLoading}
      />

      <NewSecretModal open={newSecretModalOpen} onClose={handleCloseNewSecretModal} secret={newSecret} />
    </Box>
  );
};

export default APIIntegrationTab;
